import { useLocation, useNavigate } from '@remix-run/react'
import { useCallback } from 'react'
import { aSelectedListing, useSetAtom } from '~/atoms'
import { useEmbed } from '~/hooks'
import { buildMapListingPath, buildMapRootPath } from '~/utils'
import type { GoogleDeckDomEvent } from '../google'
import type { ListingPointFeature } from './listingPointFeatures'

function isModifiedClick(domEvent: GoogleDeckDomEvent | null) {
  return (
    domEvent instanceof MouseEvent &&
    (domEvent.metaKey || domEvent.ctrlKey)
  )
}

function getSelectedListingFromFeature(feature: ListingPointFeature) {
  const { properties } = feature

  return {
    _id: properties._id,
    location: {
      coordinates: feature.geometry.coordinates,
    },
    propertyTypes: properties.propertyTypeList,
    slug: properties.slug,
    status: properties.status,
    type: properties.type,
  }
}

export function useGoogleListingClickHandlers() {
  const navigate = useNavigate()
  const location = useLocation()
  const setSelectedListing = useSetAtom(aSelectedListing)
  const { isEmbed, openLinksInNewTab } = useEmbed()

  const onListingClick = useCallback(
    (feature: ListingPointFeature, domEvent: GoogleDeckDomEvent | null) => {
      const { properties } = feature

      if (isModifiedClick(domEvent)) {
        window.open(`/listings/${properties.slug}`, '_blank')
        return
      }

      if (isEmbed && !openLinksInNewTab) {
        navigate(
          buildMapListingPath(
            location.pathname,
            properties.slug,
            location.search,
            'dialog'
          )
        )
      } else {
        navigate(
          buildMapListingPath(location.pathname, properties.slug, location.search)
        )
      }

      setSelectedListing(getSelectedListingFromFeature(feature))
    },
    [
      isEmbed,
      location.pathname,
      location.search,
      navigate,
      openLinksInNewTab,
      setSelectedListing,
    ]
  )

  const onBackgroundClick = useCallback(() => {
    const mapRootPath = buildMapRootPath(location.pathname, location.search)

    if (location.pathname !== mapRootPath.split('?')[0]) {
      navigate(mapRootPath)
    }

    setSelectedListing(null)
  }, [location.pathname, location.search, navigate, setSelectedListing])

  return {
    onBackgroundClick,
    onListingClick,
  }
}
